import { Button } from '@/components/ui/shadcn/button'
import { Textarea } from '@/components/ui/shadcn/textarea'
import { GenerateButton } from '@/components/ui/buttons/generate-button'
import { type ImageModel } from '@backend/lib/ai/functions/generateImage'
import { ImageModelPicker, type ModelUpgradeLimit } from '@/components/ui/image-model-picker'
import { ReferenceImageAttacher } from '@/components/ui/reference-image-attacher'
import { ImageGenerationSettings } from '@/components/ui/image-generation-settings'
import { type ImageResolution } from '../types'
import { toast } from 'sonner'

interface Props {
  prompt: string
  onPromptChange: (value: string) => void
  onKeyDown: (e: React.KeyboardEvent) => void
  placeholder: string
  isGenerating: boolean
  hasGeneratedImage: boolean
  onCancel: () => void
  onGenerate: () => void
  onUseImage: () => void
  useImageButtonText?: string
  imageModel?: ImageModel
  onModelChange?: (model: ImageModel) => void
  referenceImages?: string[]
  onReferenceImagesChange?: (images: string[]) => void
  maxReferenceImages?: number
  flexSteps?: number
  onFlexStepsChange?: (steps: number) => void
  flexGuidance?: number
  onFlexGuidanceChange?: (guidance: number) => void
  enhance?: boolean
  onEnhanceChange?: (value: boolean) => void
  useCaption?: boolean
  onUseCaptionChange?: (value: boolean) => void
  useColorPalette?: boolean
  onUseColorPaletteChange?: (value: boolean) => void
  hasBrandColors?: boolean
  showCaptionAndColors?: boolean
  resolution?: ImageResolution
  onResolutionChange?: (value: ImageResolution) => void
  isFreePlan?: boolean
  onUpgradeClick?: (limit: ModelUpgradeLimit) => void
}

export function ImagePromptBox({
  prompt,
  onPromptChange,
  onKeyDown,
  placeholder,
  isGenerating,
  hasGeneratedImage,
  onCancel,
  onGenerate,
  onUseImage,
  useImageButtonText = 'Accept',
  imageModel,
  onModelChange,
  referenceImages,
  onReferenceImagesChange,
  maxReferenceImages,
  flexSteps,
  onFlexStepsChange,
  flexGuidance,
  onFlexGuidanceChange,
  enhance,
  onEnhanceChange,
  useCaption,
  onUseCaptionChange,
  useColorPalette,
  onUseColorPaletteChange,
  hasBrandColors,
  showCaptionAndColors,
  resolution,
  onResolutionChange,
  isFreePlan,
  onUpgradeClick,
}: Props) {
  return (
    <div className="border-primary bg-background flex flex-col gap-2 rounded-lg border p-2">
      <Textarea
        value={prompt}
        onChange={(e) => onPromptChange(e.target.value)}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        className="text-foreground min-h-[80px] resize-none border-none bg-transparent"
        disabled={isGenerating}
      />

      <div className="flex w-full justify-between p-2">
        <div className="flex items-center gap-2">
          <Button variant="secondary" onClick={onCancel} disabled={isGenerating}>
            Cancel
          </Button>
          {referenceImages && onReferenceImagesChange && (
            <ReferenceImageAttacher
              images={referenceImages}
              onChange={onReferenceImagesChange}
              maxImages={maxReferenceImages}
              disabled={isGenerating}
              onFilesRejected={(count) =>
                toast.warning(`${count} image${count > 1 ? 's' : ''} skipped (exceeds 10 MB limit)`)
              }
            />
          )}
        </div>

        <div className="flex items-center gap-3">
          {onResolutionChange && (
            <ImageGenerationSettings
              resolution={resolution ?? '1K'}
              onResolutionChange={onResolutionChange}
              enhance={enhance}
              onEnhanceChange={onEnhanceChange}
              useCaption={useCaption ?? false}
              onUseCaptionChange={(v) => onUseCaptionChange?.(v)}
              useColorPalette={useColorPalette ?? false}
              onUseColorPaletteChange={(v) => onUseColorPaletteChange?.(v)}
              imageModel={imageModel}
              hasBrandColors={hasBrandColors}
              disabled={isGenerating}
              showCaptionAndColors={showCaptionAndColors}
              flexSteps={flexSteps}
              onFlexStepsChange={onFlexStepsChange}
              flexGuidance={flexGuidance}
              onFlexGuidanceChange={onFlexGuidanceChange}
            />
          )}
          {imageModel && onModelChange && (
            <ImageModelPicker
              value={imageModel}
              onChange={onModelChange}
              disabled={isGenerating}
              isFreePlan={isFreePlan}
              onUpgradeClick={onUpgradeClick}
            />
          )}

          {hasGeneratedImage ? (
            <>
              <Button variant="primary" onClick={onGenerate} disabled={isGenerating}>
                Retry
              </Button>
              <Button
                onClick={onUseImage}
                disabled={isGenerating}
                className="bg-green-600 text-white hover:bg-green-700">
                {useImageButtonText}
              </Button>
            </>
          ) : (
            <GenerateButton
              onClick={onGenerate}
              disabled={isGenerating || (!prompt.trim() && !(enhance && useCaption))}
              loading={isGenerating}
            />
          )}
        </div>
      </div>
    </div>
  )
}
